/**
 * <DebugBridge /> — frontend half of the Stream debug harness.
 *
 * Subscribes to the `debug://request` Tauri event channel emitted by the Rust
 * HTTP server (task 2901, `apps/stream/src-tauri/src/debug_server.rs`) and
 * dispatches each request to the matching operation (state, dom, navigate,
 * click, fill, wait, reset). Replies are emitted on `debug://response` with
 * the same correlation `id`.
 *
 * Mounted at app root inside `<ErrorBoundary>` (sibling of route content) so
 * a render-time crash in the routed content swaps the boundary fallback in
 * but leaves the bridge running. The bridge renders nothing visible.
 *
 * Side effects on mount:
 *   - Subscribes to `stream:debug:error` and pushes entries into the captured
 *     error ring buffer (decoupled from logger / ErrorBoundary).
 *   - Exposes `window.__stream_debug` for ad-hoc DevTools introspection.
 *   - Listens for `debug://request` events and emits matching responses.
 *
 * All side effects are torn down in the `useEffect` cleanup.
 */

import { useEffect } from 'react'
import type { UnlistenFn } from '@tauri-apps/api/event'
import { IS_TAURI } from '@/lib/platform'

import { router } from '@/routes'
import { logger } from '@/lib/logger'

import { collectInteractiveElements } from './dom-snapshot'
import { resolve } from './selector-resolver'
import { snapshotStores, STORE_REGISTRY } from './store-registry'
import {
  clearErrors,
  installErrorBufferListener,
  snapshotErrors,
} from './error-buffer'
import {
  clickElement,
  ContentEditableUnsupportedError,
  fillElement,
} from './click-fill'
import { waitForCondition } from './wait'
import type {
  RequestPayload,
  ResponsePayload,
  Selector,
  WaitCondition,
} from './types'

declare global {
  interface Window {
    __stream_debug?: {
      router: typeof router
      stores: typeof STORE_REGISTRY
      resolve: typeof resolve
      capturedErrors: typeof snapshotErrors
      clearErrors: typeof clearErrors
    }
  }
}

export function DebugBridge() {
  useEffect(() => {
    const detachErrorBuffer = installErrorBufferListener()

    window.__stream_debug = {
      router,
      stores: STORE_REGISTRY,
      resolve,
      capturedErrors: snapshotErrors,
      clearErrors,
    }

    let unlisten: UnlistenFn | undefined
    let cancelled = false

    // The `window.__stream_debug` handle above is useful in both targets, so it
    // is installed unconditionally. Only the Rust IPC transport is desktop-only:
    // the debug HTTP server that drives `debug://request` lives in src-tauri
    // (task 2901) and has no browser counterpart. Gating the subscription lets
    // Rollup drop `@tauri-apps/api/event` from the web bundle (Task 4093).
    if (IS_TAURI) {
      void import('@tauri-apps/api/event')
        .then(({ listen }) => {
          if (cancelled) return
          return listen<RequestPayload>('debug://request', (event) => {
            void handleRequest(event.payload)
          }).then((u) => {
            if (cancelled) {
              // Mount/unmount happened before listen() resolved — detach now.
              u()
            } else {
              unlisten = u
            }
          })
        })
        .catch((err) => {
          const msg = err instanceof Error ? err.message : String(err)
          logger.warn(`Failed to subscribe to debug://request: ${msg}`, 'DebugBridge')
        })
    }

    logger.info('DebugBridge mounted', 'DebugBridge')

    return () => {
      cancelled = true
      try {
        unlisten?.()
      } catch {
        /* ignore */
      }
      detachErrorBuffer()
      delete window.__stream_debug
    }
  }, [])

  return null
}

// ── Request dispatch ────────────────────────────────────────────────────────

async function handleRequest(req: RequestPayload): Promise<void> {
  const reply = (ok: boolean, result?: unknown, error?: string) => {
    const payload: ResponsePayload = { id: req.id, ok }
    if (result !== undefined) payload.result = result
    if (error !== undefined) payload.error = error
    // Only reachable on desktop: the sole caller is the `debug://request`
    // listener, which is never subscribed in the browser build. The explicit
    // guard is still required — Rollup only drops the import when it is behind
    // the build-time constant, not merely unreachable in practice.
    if (!IS_TAURI) return
    void import('@tauri-apps/api/event')
      .then(({ emit }) => emit('debug://response', payload))
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err)
        logger.warn(`Failed to emit debug://response: ${msg}`, 'DebugBridge')
      })
  }

  try {
    switch (req.op) {
      case 'state': {
        reply(true, {
          route: router.state.location.pathname,
          stores: snapshotStores(),
          captured_errors: snapshotErrors(),
        })
        return
      }

      case 'dom': {
        reply(true, { elements: collectInteractiveElements() })
        return
      }

      case 'navigate': {
        const route = readString(req.params, 'route')
        if (route === undefined) return reply(false, undefined, 'invalid_params:route')
        await router.navigate(route)
        // Flush a microtask so router state reflects the new pathname before
        // we read it back.
        await Promise.resolve()
        reply(true, { route: router.state.location.pathname })
        return
      }

      case 'click': {
        const selector = req.params.selector as Selector | undefined
        if (!selector || typeof selector !== 'object') {
          return reply(false, undefined, 'invalid_params:selector')
        }
        const pointer = req.params.pointer === true
        const matches = resolve(selector)
        if (matches.length === 0) return reply(false, undefined, 'selector_no_match')
        if (matches.length > 1) {
          return reply(false, undefined, `selector_ambiguous:${matches.length}`)
        }
        clickElement(matches[0], { pointer })
        reply(true, { matched: 1 })
        return
      }

      case 'fill': {
        const selector = req.params.selector as Selector | undefined
        const value = req.params.value
        if (!selector || typeof selector !== 'object') {
          return reply(false, undefined, 'invalid_params:selector')
        }
        if (typeof value !== 'string') {
          return reply(false, undefined, 'invalid_params:value')
        }
        const matches = resolve(selector)
        if (matches.length === 0) return reply(false, undefined, 'selector_no_match')
        if (matches.length > 1) {
          return reply(false, undefined, `selector_ambiguous:${matches.length}`)
        }
        const el = matches[0]
        if (
          el.isContentEditable ||
          el.getAttribute('contenteditable') === 'true' ||
          el.getAttribute('contenteditable') === ''
        ) {
          return reply(false, undefined, 'fill_unsupported_for_contenteditable')
        }
        if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) {
          return reply(false, undefined, 'fill_unsupported_for_element')
        }
        try {
          fillElement(el, value)
        } catch (err) {
          if (err instanceof ContentEditableUnsupportedError) {
            return reply(false, undefined, 'fill_unsupported_for_contenteditable')
          }
          if (err instanceof Error && err.message === 'fill_unsupported_for_element') {
            return reply(false, undefined, 'fill_unsupported_for_element')
          }
          throw err
        }
        reply(true, { matched: 1 })
        return
      }

      case 'wait': {
        const condition = req.params.condition as WaitCondition | undefined
        const timeoutMs = req.params.timeout_ms
        if (!condition || typeof condition !== 'object') {
          return reply(false, undefined, 'invalid_params:condition')
        }
        if (typeof timeoutMs !== 'number' || !Number.isFinite(timeoutMs) || timeoutMs <= 0) {
          return reply(false, undefined, 'invalid_params:timeout_ms')
        }
        const result = await waitForCondition(condition, timeoutMs)
        if (result.ok) {
          reply(true, { satisfied_at: new Date().toISOString() })
        } else {
          reply(false, undefined, result.error)
        }
        return
      }

      case 'reset': {
        clearErrors()
        await router.navigate('/events')
        reply(true)
        return
      }

      default: {
        const unknown = (req as { op: string }).op
        reply(false, undefined, `unknown_op:${unknown}`)
        return
      }
    }
  } catch (err) {
    const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err)
    logger.error(`DebugBridge handler failed for op=${req.op}: ${msg}`, 'DebugBridge')
    reply(false, undefined, msg)
  }
}

/** Read a string field from the typed `params` map, returning undefined if absent / wrong type. */
function readString(params: Record<string, unknown>, key: string): string | undefined {
  const v = params[key]
  return typeof v === 'string' ? v : undefined
}
